import React, { useEffect, useState, useCallback } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DEFAULT_PAGE } from 'configs';
import ExcelJS from 'exceljs';
import saveAs from 'file-saver';
import { formatColumn, formatRowData } from 'configs/cell_xlsx';
import { InputText, InputSelect, Card, TableListData } from 'components';
import type { UserEntity, GetListUserEntity } from 'services/openapi';
import {
  useActiveUserMutation,
  useInactiveUserMutation,
  useRemoveUserMutation,
  useGetUsersListQuery,
  useExportUsersListQuery,
  useGetCompanyAllQuery,
  useGenQrBidvVAMutation,
} from 'services';
import toast from 'react-hot-toast';
import 'moment/min/locales';
import {
  Button,
  Icon,
  InputWraper,
  useForm,
  NumberUtils,
  IUseFormStructureItem,
} from 'modules';
import { InputToggleSwitch } from '../../../components/toggle-switch';
import { PopupWraper } from '../../../components/popup';
import UpdateUser from '../update';
import AddPoint from '../addPoint';
import CreateAccount from '../create';
import {
  ActiveUserSuccess,
  ActiveUserSuccessDefined,
  FilterType,
  InactiveUserSuccess,
  InactiveUserSuccessDefined,
  RemoveUserSuccess,
  RemoveUserSuccessDefined,
  convertSelectRoleOptions,
  convertSelectStatusOptions,
  exportHeaders,
  getInitFilterValues,
} from './configs';
import { NewRoleTypes, RoleTypesAll, StatusTypes } from 'configs/constants';
import { checkValidActionRoleAdmin, getUserRoles } from 'utils/helpers';
import { useLocation } from 'react-router-dom';
import { Roles } from 'configs/roles';
import UpdatePassword from '../updatePassword';
import CreateMultiUser from '../createMultiUser';
import { normalizedPhoneNumber } from 'utils/helpers/helpers';
import { Actions } from 'configs/actions';

const UserList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);
  const [itemId, setItemId] = useState<any>();
  const [openPopUpCreateUser, setOpenPopUpCreateUser] =
    useState<boolean>(false);
  const [openPopUpCreateMultiUser, setOpenPopUpCreateMultiUser] =
    useState<boolean>(false);
  const [openPopUpUpdateUser, setOpenPopUpUpdateUser] =
    useState<boolean>(false);
  const [openPopUpRemoveUser, setOpenPopUpRemoveUser] =
    useState<boolean>(false);
  const [openPopUpAddPoint, setOpenPopUpAddPoint] = useState<boolean>(false);
  const [openPopUpUpdatePassword, setOpenPopUpUpdatePassword] =
    useState<boolean>(false);
  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [refetchDataCurrentPage, setRefetchDataCurrentPage] = useState(0);
  const [dataTable, setDataTable] = useState([]);

  const { data: companyInfo } = useGetCompanyAllQuery();
  // const dataCompany = companyInfo?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
  const checkedDataCompany =
    companyInfo?.map((item) => ({
      label: `${item.compName}`,
      value: item.compId,
    })) || [];

  const {
    mutateAsync: mutateActiveUserAsync,
    data: dataActiveUser,
    error: errorActiveUser,
  } = useActiveUserMutation();

  const {
    mutateAsync: mutateInactiveUserAsync,
    data: dataInactiveUser,
    error: errorInactiveUser,
  } = useInactiveUserMutation();
  const {
    mutateAsync: mutateRemoveUserAsync,
    data: dataRemoveUser,
    error: errorRemoveUser,
  } = useRemoveUserMutation();

  const { mutateAsync: mutateGenQrAsync, isLoading: isLoadingGenQr } =
    useGenQrBidvVAMutation();

  const { data: leadersList, refetch: refetchLeadersList } = {
    data: { data: [] },
    refetch: () => {},
  };

  const convertVariables = (variables: FilterType) => {
    const { isActive, role: tRole, searchDetailContent, ...rest } = variables;
    let role = {};
    let objectType = {};
    if (tRole === NewRoleTypes['Merchant'] || tRole === NewRoleTypes['Admin']) {
      role = { role: tRole };
    } else if (tRole !== NewRoleTypes['Tất cả']) {
      objectType = { objectType: tRole };
      role = { role: 'Agency' };
    }

    return {
      ...rest,
      ...role,
      ...objectType,
      ...(isActive && isActive === StatusTypes['Tất cả'] ? {} : { isActive }),
      ...(searchDetailContent ? { searchDetailContent } : {}),
    };
  };

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const { isFetching: isLoadingExport, refetch } = useExportUsersListQuery({
    ...convertVariables(variables),
    page: DEFAULT_PAGE.toString(),
    pageSize: String((dataTable as unknown as GetListUserEntity)?.total) ?? 100,
  });

  const handleChangeStatusUser = async (item: any) => {
    if (item?.isActive === false) {
      try {
        await mutateActiveUserAsync({ userId: item?.id });
        // setVariables((state) => ({ ...state, refreshData: state.refreshData + 1 }));
        // reaload page
        setRefetchDataCurrentPage((prev) => prev + 1);
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        await mutateInactiveUserAsync({ userId: item?.id });
        // setVariables((state) => ({ ...state, refreshData: state.refreshData + 1 }));
        // reaload page
        setRefetchDataCurrentPage((prev) => prev + 1);
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const handleRemoveUser = async () => {
    try {
      await mutateRemoveUserAsync({ userId: itemId });
      refetchLeadersList();
      setOpenPopUpRemoveUser(false);
      // setVariables((state) => ({ ...state, refreshData: state.refreshData + 1 }));
      // reaload page
      setRefetchDataCurrentPage((prev) => prev + 1);
    } catch (error) {
      console.log('error', error);
    }
  };

  const normalizedRole = (role: string) => {
    switch (role) {
      case 'Agency':
        return 'Đại lý';
      case 'Leader':
        return 'Trưởng nhóm';
      case 'SpVisor':
        return 'Nhân viên đối soát';
      case 'OilMc':
        return 'Merchant';
      default:
        return role;
    }
  };

  const normalizedSubRole = (role: string) => {
    switch (role) {
      case 'Staff':
        return 'Nhân viên';
      case 'Driver':
        return 'Tài xế';
      case 'Partner':
        return 'Đối tác';
      case 'Customer':
        return 'Nhà cung cấp';
      default:
        return role;
    }
  };

  const handleExportExcel = async () => {
    const allData = await refetch({
      ...convertVariables(variables),
      page: DEFAULT_PAGE,
      pageSize: String((dataTable as unknown as GetListUserEntity)?.total),
    } as any);

    //
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Columns
    const headerColumn = exportHeaders.map((header) => {
      return {
        header: header,
        key: header,
        width: 40,
      };
    });
    worksheet.columns = headerColumn;

    const firstRow = worksheet.getRow(1);
    firstRow.eachCell((cell) => formatColumn(cell));

    // Data
    allData?.data?.data?.map((item: UserEntity, index: number) => {
      const row = worksheet.addRow([
        index + 1,
        normalizedPhoneNumber(item.phoneNumber || ''),
        item.fullName,
        item.citizenIdentityCardNo,
        item.accountNumber,
        item.bidvVaPrefix,
        item.bidvVaPayerId,
        item.compName,
        // item.agencyName,
        NumberUtils.toFormatNumber(Number(item.balance)),
        item.email,
        item.referralId || '',
        item.isActive ? 'Hoạt động' : 'Không hoạt động',
      ]);
      row.eachCell((cell) => formatRowData(cell));
    });

    // Generate Excel and trigger file download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const now = new Date();
    saveAs(
      blob,
      `Danh_sach_nguoi_dung_${now
        .toLocaleTimeString('vn-VN', { hour12: false })
        .replace(/:/g, '_')}_${now
        .toLocaleDateString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
        .replace(/\//g, '_')}.xlsx`,
    );
  };

  useEffect(() => {
    if (dataActiveUser) {
      const { statusCode } = dataActiveUser;

      const dataDefined =
        ActiveUserSuccessDefined[statusCode as ActiveUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }
  }, [errorActiveUser, dataActiveUser]);

  useEffect(() => {
    if (dataInactiveUser) {
      const { statusCode } = dataInactiveUser;

      const dataDefined =
        InactiveUserSuccessDefined[statusCode as InactiveUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }
  }, [errorInactiveUser, dataInactiveUser]);

  useEffect(() => {
    if (dataRemoveUser) {
      const { statusCode } = dataRemoveUser;

      const dataDefined =
        RemoveUserSuccessDefined[statusCode as RemoveUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }
  }, [dataRemoveUser, errorRemoveUser]);

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'searchString',
      label: 'Tài khoản',
      defaultValue: initFilterValues.searchString,
    },
    {
      name: 'role',
      label: 'Vai trò',
      defaultValue: initFilterValues.role,
    },
    {
      name: 'isActive',
      label: 'Trạng thái',
      defaultValue: initFilterValues.isActive,
    },
  ];

  const columnsDefined: ColumnDef<UserEntity>[] = [
    {
      accessorKey: 'phoneNumber',
      header: 'Tài khoản',
      cell: (info) => normalizedPhoneNumber(String(info.getValue())),
    },
    {
      accessorKey: 'fullName',
      header: 'Tên tài khoản',
    },
    {
      accessorKey: 'citizenIdentityCardNo',
      header: 'CCCD/MST',
    },
    {
      accessorKey: 'accountNumber',
      header: 'Tiểu khoản TPBank',
    },
    {
      accessorKey: 'bidvVaPrefix',
      header: 'Mã đầu Tiểu khoản BIDV',
    },
    {
      accessorKey: 'bidvVaPayerId',
      header: 'Tiểu khoản BIDV',
    },
    {
      accessorKey: 'role',
      header: 'Vai trò',
      cell: (info) => {
        if (info.getValue() !== Roles.AGENCY) {
          return (
            <div style={{ width: 150 }}>
              {normalizedRole(String(info.getValue()))}
            </div>
          );
        } else {
          return (
            <div style={{ width: 150 }}>
              {normalizedSubRole(String(info.row.original.objectType))}
            </div>
          );
        }
      },
    },
    {
      accessorKey: 'compName',
      header: 'Hợp tác xã',
      cell: (info) => {
        return <div style={{ width: 150 }}>{String(info.getValue())}</div>;
      },
    },
    // {
    //   accessorKey: "agencyName",
    //   header: "Phòng ban",
    //   cell: (info) => {
    //     return (
    //       <div style={{ width: 150 }}>{String(info.getValue())}</div>
    //     );
    //   },
    // },
    {
      accessorKey: 'balance',
      header: 'Số dư (đ)',
      cell: (info) => NumberUtils.toFormatNumber(Number(info.getValue())),
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'myReferralId',
      header: 'Mã giới thiệu',
    },
    {
      accessorKey: 'isActive',
      header: 'Trạng thái',
      cell: (info) => {
        return (
          <InputToggleSwitch
            onChange={() =>
              (getUserRoles().includes(Roles.SUPER_ADMIN) ||
                getUserRoles().includes(Roles.ADMIN)) &&
              checkValidActionRoleAdmin(Actions.ManageUser_ActiveUser) &&
              checkValidActionRoleAdmin(Actions.ManageUser_DeactiveUser) &&
              handleChangeStatusUser(info.row.original)
            }
            value={info.getValue()}
            onTouched={() => false}
            label=''
            name=''
          />
        );
      },
    },
    {
      accessorKey: 'vietQRImage',
      header: 'Mã QR BIDV',
      cell: (info) => {
        const vietQRImage = info.getValue() as string;
        const rowData = info.row.original;

        if (vietQRImage) {
          // Nếu có QR image thì hiển thị image
          return (
            <div style={{ textAlign: 'center' }}>
              <img
                src={`data:image/png;base64,${vietQRImage}`}
                alt='QR Code'
                style={{
                  width: '60px',
                  height: '80px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                }}
              />
            </div>
          );
        } else {
          // Nếu chưa có QR image thì hiển thị button Gen QR
          return (
            <div style={{ textAlign: 'center' }}>
              <Button
                className='whitespace-nowrap'
                label='Gen QR'
                style={{
                  whiteSpace: 'nowrap',
                }}
                buttonType='info'
                isLoading={isLoadingGenQr}
                onClick={async () => {
                  try {
                    if (!rowData.bidvVaPayerId) {
                      toast.error(
                        'Thiếu thông tin Tiểu khoản BIDV',
                      );
                      return;
                    }
                    await mutateGenQrAsync({
                      endCode: rowData.bidvVaPayerId || '',
                      requestBody: {
                        amount: '0',
                        description: '',
                      },
                    });
                    toast.success('Tạo mã QR thành công!');
                    setRefetchDataCurrentPage((prev) => prev + 1);
                  } catch (error) {
                    toast.error('Có lỗi xảy ra khi tạo mã QR');
                  }
                }}
              />
            </div>
          );
        }
      },
    },
    {
      id: 'action',
      header: 'Thao tác',
      cell: (info) =>
        (getUserRoles().includes(Roles.SUPER_ADMIN) ||
          getUserRoles().includes(Roles.ADMIN)) && (
          <div className='action'>
            {checkValidActionRoleAdmin(Actions.ManageUser_UpdateUser) && (
              <div
                className='action__update'
                onClick={() => {
                  setOpenPopUpUpdateUser(true);
                  setItemId(info.row.original.id);
                }}
              >
                <Icon.ActionEditIcon />
              </div>
            )}

            {checkValidActionRoleAdmin(Actions.ManageUser_RemoveUser) && (
              <div
                className='action__remove'
                onClick={() => {
                  setOpenPopUpRemoveUser(true);
                  setItemId(info.row.original.id);
                }}
              >
                <Icon.Trash />
              </div>
            )}

            {checkValidActionRoleAdmin(Actions.ManageUser_AddPointToUser) && (
              <div
                className='action__plusminus'
                onClick={() => {
                  setOpenPopUpAddPoint(true);
                  setItemId(info.row.original.id);
                }}
              >
                <Icon.PlusMinus />
              </div>
            )}

            {checkValidActionRoleAdmin(
              Actions.ManageUser_UpdateUserPassword,
            ) && (
              <div
                className='action__updatepassword'
                onClick={() => {
                  setOpenPopUpUpdatePassword(true);
                  setItemId(info.row.original.id);
                }}
              >
                <Icon.LockUserIcon />
              </div>
            )}
          </div>
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('searchString')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('role')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectRoleOptions()}
                  />
                )}
              />
            </div>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('isActive')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>
          </div>
          {checkValidActionRoleAdmin(Actions.ManageUser_ListUser) && (
            <div className='textCenter mt16 wrapButtonFilter'>
              <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
              <Button
                className='ml15'
                onClick={handleExportExcel}
                label={'Tải về DS'}
                type='submit'
                buttonType='info'
                isLoading={isLoadingExport}
              />
            </div>
          )}
        </form>
      </Card>

      {/* {getUserRoles().includes("Admin") && (
        <div className="textCenter">
          <Button className="mb20 " label={"Tạo mới tài khoản"} onClick={() => setOpenPopUpCreateUser(true)} buttonType="success" />
        </div>
      )} */}

      <TableListData<GetListUserEntity>
        columns={columnsDefined as ColumnDef<GetListUserEntity>[]}
        useQuery={useGetUsersListQuery}
        variables={variables}
        refetchDataCurrentPage={refetchDataCurrentPage}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        orderColumn
        callBackData={setDataTable}
        customHeaderBtnGroup={() => (
          <>
            {(getUserRoles().includes(Roles.SUPER_ADMIN) ||
              getUserRoles().includes(Roles.ADMIN)) && (
              <div
                className='textCenter ml5 mt20'
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {checkValidActionRoleAdmin(Actions.ManageUser_CreateUser) && (
                  <Button
                    className='mb20'
                    label={'Tạo mới tài khoản'}
                    onClick={() => setOpenPopUpCreateUser(true)}
                    buttonType='success'
                  />
                )}
                {checkValidActionRoleAdmin(
                  Actions.ManageUser_CreateMultipleUser,
                ) && (
                  <Button
                    className='mb20'
                    label={'Tạo nhiều tài khoản'}
                    onClick={() => setOpenPopUpCreateMultiUser(true)}
                    buttonType='info'
                  />
                )}
              </div>
            )}
          </>
        )}
        customTopRow={(data: any) => {
          if (data) {
            return (
              <>
                <tr>
                  <td colSpan={6}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(
                        Number(data?.totalBalance ?? 0),
                      )}
                    </strong>
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            );
          }
        }}
      />

      {openPopUpCreateUser && (
        <CreateAccount
          onClose={() => setOpenPopUpCreateUser(false)}
          refetchLeadersList={() => refetchLeadersList()}
          forceUpdateTable={() =>
            setVariables((state) => ({
              ...state,
              refreshData: state.refreshData + 1,
            }))
          }
        />
      )}
      {openPopUpCreateMultiUser && (
        <CreateMultiUser
          onClose={() => setOpenPopUpCreateMultiUser(false)}
          forceUpdateTable={() =>
            setVariables((state) => ({
              ...state,
              refreshData: state.refreshData + 1,
            }))
          }
        />
      )}
      {openPopUpUpdateUser && (
        <UpdateUser
          userId={itemId}
          onClose={() => setOpenPopUpUpdateUser(false)}
          refetchLeadersList={() => refetchLeadersList()}
          forceUpdateTable={() => setRefetchDataCurrentPage((prev) => prev + 1)}
        />
      )}
      {openPopUpRemoveUser && (
        <PopupWraper
          center
          title='Xóa tài khoản'
          onClose={() => setOpenPopUpRemoveUser(false)}
        >
          <div className='container'>
            <div className='row'>
              <div className='col-12'>
                <div className='label'>
                  Bạn có chắc chắn muốn xóa tài khoản này không?
                </div>
              </div>
            </div>
          </div>

          <div className='wrap-button-remove-user'>
            <Button
              label='Xóa'
              buttonType='success'
              onClick={() => handleRemoveUser()}
            />
            <Button
              label='Hủy'
              buttonType='grey'
              onClick={() => setOpenPopUpRemoveUser(false)}
            />
          </div>
        </PopupWraper>
      )}
      {openPopUpAddPoint && (
        <AddPoint
          onClose={() => setOpenPopUpAddPoint(false)}
          userId={itemId}
          forceUpdateTable={() => setRefetchDataCurrentPage((prev) => prev + 1)}
        />
      )}
      {openPopUpUpdatePassword && (
        <UpdatePassword
          onClose={() => setOpenPopUpUpdatePassword(false)}
          userId={itemId}
          forceUpdateTable={() => setRefetchDataCurrentPage((prev) => prev + 1)}
        />
      )}
    </>
  );
};

export default UserList;
